import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { SharingGridButton, formatNumber, useProject } from 'shared'
import { DonateButton, SubscribeButton, CreateFundraisingEventButton, MatchingEventNotice } from 'ppp'
import { TaxNotice, MatchingFundHelper, GtmHelper, formatCents, findApiV4Link } from 'shared'
import { ProgressBarProps, StatsProps, MatchingFundNoticeProps, ClosedNoticeProps, MatchingFund } from './types'

export const Stats = ({
  apiUrl,
  matchingEventUrl,
  allowFundraisingEvents,
  createFundraisingEventUrl,
  fundraisingEventLandingpageUrl,
  newsletterSubscriptionUrl,
  shareUrl,
}: StatsProps): JSX.Element => {
  const { loading, status, ...project } = useProject(apiUrl)
  const [activeMatchingFund, setActiveMatchingFund] = useState<MatchingFund | null>(null)
  useEffect(() => {
    if (loading || !project.id) return
    GtmHelper.pushProductDetailImpression(project)
    const mf = MatchingFundHelper.locallyActiveMatchingFund(project)
    mf && setActiveMatchingFund(mf)
  }, [loading, project])

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        flexDirection: 'column',
      }}
    >
      <div>
        <ProgressBar percentage={project.progress_percentage} status={status} />
        <div className={classNames('generic-big-numbers is-compressed', `is-${status}`)}>
          {status === 'completed' ? <h4>{I18n.t('projects.show.stats.completed_label')}</h4> : null}
          <ul className="list-unstyled">
            <li className="is-left">
              <span className="number">{project.progress_percentage} %</span>
              <span className="legend">{I18n.t('projects.show.stats.percentage_label')}</span>
            </li>
            <li className="is-middle">
              <span className="number">{formatNumber(project.donations_count)}</span>
              <span className="legend">{I18n.t('projects.show.stats.donations_count_label')}</span>
            </li>
            {project.completed_at || project.closed_at ? (
              <li className="is-right">
                <span className="number">{formatCents(project.donated_amount_in_cents)}</span>
                <span className="legend">{I18n.t('projects.show.stats.donated_amount_label')}</span>
              </li>
            ) : (
              <li className="is-right">
                <span className="number">{formatCents(project.open_amount_in_cents)}</span>
                <span className="legend">{I18n.t('projects.show.stats.open_amount_label')}</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div>
        <MatchingEventNotice project={project} status={status} matchingEventUrl={matchingEventUrl} />
        <MatchingFundNotice activeMatchingFund={activeMatchingFund} />
        <DonateButton
          project={project}
          status={status}
          className="btn-block btn-large mb-3"
          activeMatchingFund={activeMatchingFund}
          gaAction="click donate_button__top"
        />

        {project.donations_prohibited || <TaxNotice as="p" className="mb-3" />}

        <div className="mb-3">
          {allowFundraisingEvents === 'true' && (
            <CreateFundraisingEventButton
              ctaUrl={createFundraisingEventUrl}
              readmoreUrl={fundraisingEventLandingpageUrl}
            />
          )}
          <SubscribeButton project={project} url={newsletterSubscriptionUrl} />
        </div>

        <SharingGridButton
          shareUrl={shareUrl}
          gaLabel="ppp_stats"
          platforms={['email', 'facebook', 'twitter', 'link', 'whatsapp', 'messenger']}
          modalHeader={I18n.t('projects.show.stats.share_modal_headline')}
          buttonLabel={I18n.t('projects.show.stats.share_button_label')}
          className="btn-large mb-2"
        />

        <ClosedNotice {...project.closed_notice} />
      </div>
    </div>
  )
}

/**
 * @info matching fund logo image should be sized 280x72
 */
const MatchingFundNotice = ({ hidden, activeMatchingFund }: MatchingFundNoticeProps): JSX.Element | null => {
  if (hidden || !activeMatchingFund) return null
  return (
    <div className="matching-fund-notice">
      <img src={activeMatchingFund.logo_url} alt={activeMatchingFund.title} />
      <p>{I18n.t('projects.show.stats.matching_fund_notice')}</p>
    </div>
  )
}

const ProgressBar = ({ status, percentage }: ProgressBarProps): JSX.Element | null => {
  if (status === 'completed') return null
  return (
    <div className="progress generic-progress-bar mb-4">
      <div className="bar" style={{ width: `${percentage}%` }}></div>
    </div>
  )
}

const ClosedNotice = (notice: ClosedNoticeProps): JSX.Element | null => {
  const url = notice && findApiV4Link({ ofObject: notice, withRel: 'call_to_action' })
  if (!url) return null
  return (
    <a href={url} target="_blank" rel="noreferrer">
      {notice?.text}
    </a>
  )
}
